import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, from, switchMap, throwError } from 'rxjs';
import { APP_CONFIG } from '../../../core/config/app-config';
import { AuthSessionService } from '../../../services/auth/auth-session.service';
import { SKIP_AUTH_TOKEN_REFRESH } from './auth-request.context';
import { AuthTokenService } from '../../../services/auth/auth-token.service';

const EXCLUDED_ENDPOINTS = [
  '/api/registro/social',
  '/api/autenticacion/login',
  '/api/autenticacion/login-cliente',
  '/api/autenticacion/login-conductor',
  '/api/autenticacion/refrescar-token',
  '/api/autenticacion/eliminar-token',
];

/**
 * Antes de cada llamada autenticada al API configurado, solicita un JWT nuevo y lo
 * adjunta como Authorization: Bearer. Los endpoints públicos quedan excluidos.
 */
export const authTokenInterceptor: HttpInterceptorFn = (request, next) => {
  const config = inject(APP_CONFIG);
  const authSession = inject(AuthSessionService);
  const authToken = inject(AuthTokenService);

  if (
    request.context.get(SKIP_AUTH_TOKEN_REFRESH) ||
    !isProtectedApiRequest(request.url, [
      config.authApiBaseUrl,
      config.tripApiBaseUrl,
      config.adminApiBaseUrl,
      config.supportApiBaseUrl,
    ]) ||
    isExcludedEndpoint(request.url)
  ) {
    return next(request);
  }

  if (!authSession.token()) {
    return next(request);
  }

  return from(authToken.refresh()).pipe(
    switchMap((refreshedToken) =>
      next(
        request.clone({
          setHeaders: { Authorization: `Bearer ${refreshedToken}` },
        }),
      ),
    ),
    catchError((error: unknown) => {
      if (error instanceof HttpErrorResponse && (error.status === 401 || error.status === 403)) {
        authSession.clear();
      }

      return throwError(() => error);
    }),
  );
};

function isProtectedApiRequest(url: string, configuredBaseUrls: readonly string[]): boolean {
  return configuredBaseUrls.some((configuredBaseUrl) => {
    const baseUrl = configuredBaseUrl.replace(/\/$/, '');
    return url === baseUrl || url.startsWith(`${baseUrl}/`);
  });
}

function isExcludedEndpoint(url: string): boolean {
  const path = url.split('?')[0] ?? url;
  return EXCLUDED_ENDPOINTS.some((endpoint) => path.endsWith(endpoint));
}
